import {
    SilSubsystem,
    SilSubsystemCreate,
    SilSubsystemBlockCreate,
    SilProjectDevice,
    SilProjectDeviceNested,
    SilSubsystemBlock,
} from "server/models"
import { SilDeviceFilter } from "client/utils"
import {
    LoadSilProjectDevicesByFilter,
    SaveDevice,
    AddSilNestedDevice,
} from "client/store"
import { POST, DELETE } from "client/store/utils"

export const AddSilSubsystem = (subsystem: SilSubsystemCreate) => (
    POST(
        "/silSubsystem/add",
        JSON.stringify(subsystem),
    ).then((res) => {
        const newSubsystem = res as SilSubsystem
        return newSubsystem.id || null
    })
)

export const DeleteSilSubsystems = (safetyFunctionId: number) => (
    DELETE(`/silSubsystem/deleteAll/${safetyFunctionId}`)
    .then((removedRows: number) => removedRows)
)

export const AddSilSubsystemBlock = (block: SilSubsystemBlockCreate) => (
    POST(
        "/silSubsystemBlock/add",
        JSON.stringify(block),
    )
)

export const SaveSilSubsystems = (
    projectId: number,
    safetyFunctionId: number,
    subsystemList: readonly SilSubsystem[],
) => {
    /*
        When we copy function to another project, first we need to copy devices,
        because devices and subsystems created on project level will have new ids
        (new "projectDeviceId" in subsystem block)
    */
    const allBlocks: SilSubsystemBlock[] = []
    const newProjectDeviceIds: { [oldId: number]: number } = {} // { oldId: newId }
    let indexOneByOne: number = 0

    subsystemList.forEach((subsystem) => {
        subsystem.blocks.forEach((block) => {
            allBlocks.push(block)
        })
    })

    const addDevicesOneByOne = (): Promise<any> => {
        /*
            Add project devices one by one.
            If to use "Promise.all" and there are several the same devices in the list -
            the system will add all of them to the DB
        */
        const block = allBlocks[indexOneByOne]
        if (block) {
            // if project level device was already copied to another project
            if (block.projectDeviceId && newProjectDeviceIds[block.projectDeviceId]) {
                indexOneByOne += 1
                return addDevicesOneByOne()
            }

            const filters: SilDeviceFilter = {
                projectId,
                deviceId: block.deviceId,
                projectDeviceId: block.projectDeviceId,
                qidProductId: block.qidProductId,
                qidModelId: block.qidModelId,
                qidSafetyFunctionId: block.qidSafetyFunctionId,
                qidOptionId: block.qidOptionId,
                qidDatapointId: block.qidDatapointId,
                qidMethod: block.qidMethod,
            }

            return LoadSilProjectDevicesByFilter(filters).then((projectDevices) => {
                // if project level already has this device - stop execution
                if (projectDevices.length > 0 || !block.projectDevice) {
                    indexOneByOne += 1
                    return addDevicesOneByOne()
                }

                // when we copy safety function to another project - we need to clear id and set new projectId
                const newProjectDeviceData = {
                    ...block.projectDevice,
                    projectId,
                }
                delete newProjectDeviceData.id
                delete newProjectDeviceData.createdAt
                delete newProjectDeviceData.updatedAt

                return SaveDevice(null, newProjectDeviceData).then((res) => {
                    const newProjectDevice = res as SilProjectDevice

                    // if project level device was copied to another project
                    if (newProjectDevice.deviceId === null && newProjectDevice.qidProductId === null) {
                        newProjectDeviceIds[block.projectDeviceId] = newProjectDevice.id
                    }

                    // copy data from nested devices
                    const promiseNestedArray: Promise<any>[] = []
                    block.projectDevice.nestedDevices.forEach((nested) => {
                        let newNested = {
                            ...nested,
                            parentDeviceId: newProjectDevice.id || null,
                        }
                        delete newNested.id
                        delete newNested.createdAt
                        delete newNested.updatedAt

                        // if we copy to another project - clear link to device created on project level
                        if (block.projectDevice.projectId !== projectId) {
                            newNested = {
                                ...newNested,
                                projectDeviceId: null,
                            } as SilProjectDeviceNested
                        }

                        promiseNestedArray.push(
                            AddSilNestedDevice(projectId, newNested),
                        )
                    })
                    return Promise.all(promiseNestedArray).then(() => {
                        indexOneByOne += 1
                        return addDevicesOneByOne()
                    })
                })
            })
        }
        return null
    }

    // add all connected devices to project level
    return addDevicesOneByOne().then(() => (
        // clear all existing subsystems
        DeleteSilSubsystems(safetyFunctionId).then(() => {
            const promiseAddSubsystems: Promise<any>[] = []
            subsystemList.forEach((subsystem, subsystemIndex) => {
                const newSubsystem: SilSubsystem = {
                    ...subsystem,
                    id: null, // important! clear old ID
                    safetyFunctionId,
                    order: subsystemIndex,
                }
                // save subsystems and blocks
                promiseAddSubsystems.push(
                    AddSilSubsystem(newSubsystem).then((newSubsystemId) => {
                        const promiseAddBlocks: Promise<any>[] = []
                        subsystem.blocks.forEach((block, blockIndex) => {
                            const newBlock: SilSubsystemBlock = {
                                ...block,
                                id: null, // important! clear old ID
                                subsystemId: newSubsystemId,
                                projectDeviceId: block.projectDeviceId && newProjectDeviceIds[block.projectDeviceId]
                                    ? newProjectDeviceIds[block.projectDeviceId]
                                    : block.projectDeviceId,
                                order: blockIndex,
                            }
                            promiseAddBlocks.push(
                                AddSilSubsystemBlock(newBlock),
                            )
                        })

                        return Promise.all(promiseAddBlocks)
                    }),
                )
            })

            return Promise.all(promiseAddSubsystems)
        })
    ))
}
